import { useLang } from "../../i18n/LangContext";
import { Textarea } from "../ui/textarea";
import { PlayCircle, FileText, Image as ImageIcon, FileDown, ListChecks, HelpCircle, PenLine } from "lucide-react";
import { L, ytEmbed } from "./lib";

const TYPE_ICONS = { VIDEO: PlayCircle, TEXT: FileText, IMAGE: ImageIcon, PDF: FileDown, CHECKLIST: ListChecks, QUIZ: HelpCircle, REFLECTION: PenLine };

function VideoBlock({ item, draft, setDraft }) {
  const { t } = useLang();
  if (!item.url) return null;
  return (
    <div className="space-y-2">
      <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/10">
        <iframe src={ytEmbed(item.url)} title="video" className="h-full w-full" allowFullScreen />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" data-testid="day-video-checkbox" checked={draft.video}
          onChange={(e) => setDraft({ ...draft, video: e.target.checked })} className="accent-[#10B981]" />
        {t("edu.markVideoWatched")}
      </label>
    </div>
  );
}

function MaterialBlock({ item }) {
  const { t, lang } = useLang();
  return (
    <div className="space-y-2">
      {item.type === "IMAGE" && item.url && <img src={item.url} alt="" className="rounded-xl border border-white/10 max-h-80 object-cover w-full" />}
      {item.type === "PDF" && item.url && (
        <a href={item.url} target="_blank" rel="noreferrer" className="text-sm text-red-400 hover:text-red-300 underline">{t("edu.openPdf")}</a>
      )}
      {L(item.body, lang) && <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{L(item.body, lang)}</p>}
    </div>
  );
}

function ChecklistBlock({ item, draft, setDraft }) {
  const { lang } = useLang();
  const toggle = (i, checked) =>
    setDraft({ ...draft, checklist: checked ? [...draft.checklist, i] : draft.checklist.filter((x) => x !== i) });
  return (
    <div className="space-y-2">
      {item.items.map((it, i) => (
        <label key={`${item.content_id}-item-${i}`} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-300 cursor-pointer hover:bg-white/5">
          <input type="checkbox" data-testid={`day-checklist-${i}`} checked={draft.checklist.includes(i)}
            onChange={(e) => toggle(i, e.target.checked)} className="accent-[#10B981]" />
          {L(it, lang)}
        </label>
      ))}
    </div>
  );
}

function QuizBlock({ item, draft, setDraft }) {
  const { lang } = useLang();
  return item.questions.map((q, qi) => (
    <div key={`${item.content_id}-q-${qi}`} className="space-y-2">
      <div className="text-sm font-medium text-white">{qi + 1}. {L(q.question, lang)}</div>
      <div className="grid gap-2">
        {q.options.map((opt, oi) => (
          <label key={`${item.content_id}-q-${qi}-o-${oi}`} data-testid={`day-quiz-${qi}-${oi}`}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${draft.answers[qi] === oi ? "border-[#10B981]/60 bg-[#10B981]/10 text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
            <input type="radio" name={`q-${qi}`} checked={draft.answers[qi] === oi}
              onChange={() => setDraft({ ...draft, answers: { ...draft.answers, [qi]: oi } })} className="accent-[#10B981]" />
            {L(opt, lang)}
          </label>
        ))}
      </div>
    </div>
  ));
}

function ReflectionBlock({ item, draft, setDraft }) {
  const { t, lang } = useLang();
  return (
    <div className="space-y-2">
      {L(item.body, lang) && <p className="text-xs text-slate-500">{L(item.body, lang)}</p>}
      <Textarea data-testid="day-reflection-input" value={draft.reflection} rows={4}
        onChange={(e) => setDraft({ ...draft, reflection: e.target.value })}
        className="bg-[#1C202E] border-white/10 text-white" placeholder={t("edu.reflectionPlaceholder")} />
    </div>
  );
}

export function DayContentBlock({ item, draft, setDraft }) {
  const { t, lang } = useLang();
  const Icon = TYPE_ICONS[item.type] || FileText;
  return (
    <div data-testid={`day-content-${item.type}`} className="space-y-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
        <Icon className="h-3.5 w-3.5" /> {L(item.title, lang) || t(`edu.type_${item.type}`)}
      </div>
      {item.type === "VIDEO" && <VideoBlock item={item} draft={draft} setDraft={setDraft} />}
      {["TEXT", "IMAGE", "PDF"].includes(item.type) && <MaterialBlock item={item} />}
      {item.type === "CHECKLIST" && <ChecklistBlock item={item} draft={draft} setDraft={setDraft} />}
      {item.type === "QUIZ" && <QuizBlock item={item} draft={draft} setDraft={setDraft} />}
      {item.type === "REFLECTION" && <ReflectionBlock item={item} draft={draft} setDraft={setDraft} />}
    </div>
  );
}
